/**
 * ResponseCard — per-action response renderer.
 *
 * The device's reply comes back as a plain dict from cms-server's
 * asdict(call_result.<Action>Payload). This component dispatches by action
 * to a focused renderer; the generic fallback handles anything not yet
 * specialised.
 */
import React, { useState } from "react";
import { Check, AlertTriangle, X, Edit2, Clock } from "lucide-react";
import type { RemoteCommandResponse, RemoteCommandCatalogEntry } from "../../../../../api/maintenance";

interface Props {
  result: RemoteCommandResponse;
  catalogEntry: RemoteCommandCatalogEntry | null;
  onEditConfigKey?: (key: string, value: string) => void;
  onSeedSendLocalList?: (version: number) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type PillKind = "accepted" | "rejected" | "warning" | "info" | "neutral";

const STATUS_PILLS: Record<string, PillKind> = {
  Accepted: "accepted",
  Unlocked: "accepted",
  Scheduled: "info",
  RebootRequired: "warning",
  NotSupported: "warning",
  NotImplemented: "warning",
  Rejected: "rejected",
  UnlockFailed: "rejected",
  Failed: "rejected",
  Unknown: "warning",
  Faulted: "rejected",
  Occupied: "warning",
  Unavailable: "rejected",
  VersionMismatch: "warning",
  UnknownMessageId: "warning",
  UnknownVendorId: "warning",
};

function statusKind(status: string | undefined): PillKind {
  if (!status) return "neutral";
  return STATUS_PILLS[status] ?? "neutral";
}

function StatusPill({ status, kind }: { status: string; kind?: PillKind }) {
  const k = kind ?? statusKind(status);
  const Icon =
    k === "accepted" ? Check :
    k === "rejected" ? X :
    k === "warning"  ? AlertTriangle :
    Clock;
  return (
    <span className={`rcmd-response-pill is-${k}`}>
      <Icon size={12} />
      {status}
    </span>
  );
}

function Latency({ ms }: { ms?: number | null }) {
  if (ms == null) return null;
  return <span className="rcmd-response-latency">{ms.toFixed(0)} ms</span>;
}

function RawJson({ data }: { data: Record<string, unknown> }) {
  return (
    <div className="rcmd-response-raw">
      <details>
        <summary>Raw response</summary>
        <pre>{JSON.stringify(data, null, 2)}</pre>
      </details>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-action renderers
// ---------------------------------------------------------------------------

interface ConfigKeyRow {
  key: string;
  readonly?: boolean;
  value?: string;
}

const GetConfigurationRenderer: React.FC<{
  data: Record<string, unknown>;
  onEdit?: (key: string, value: string) => void;
}> = ({ data, onEdit }) => {
  const keys = (data.configuration_key as ConfigKeyRow[] | undefined) ?? [];
  const unknown = (data.unknown_key as string[] | undefined) ?? [];
  const [filter, setFilter] = useState("");
  const filtered = keys.filter((k) =>
    !filter || k.key.toLowerCase().includes(filter.toLowerCase()),
  );

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "8px 0" }}>
        <span className="rcmd-response-pill is-info">{keys.length} key{keys.length === 1 ? "" : "s"}</span>
        {unknown.length > 0 && (
          <span className="rcmd-response-pill is-warning">
            {unknown.length} unknown
          </span>
        )}
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter…"
          style={{
            marginLeft: "auto", padding: "4px 8px",
            border: "1px solid #e0e0e0", borderRadius: 4, fontSize: 12,
          }}
        />
      </div>
      <table className="rcmd-response-table">
        <thead>
          <tr>
            <th>Key</th>
            <th>Value</th>
            <th>RO</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((row, i) => (
            <tr key={`${row.key}-${i}`}>
              <td><code style={{ fontSize: 11 }}>{row.key}</code></td>
              <td className={row.readonly ? "is-readonly" : ""}>{row.value ?? "—"}</td>
              <td className={row.readonly ? "is-readonly" : ""}>{row.readonly ? "✓" : ""}</td>
              <td className="rcmd-cell-actions">
                {!row.readonly && onEdit && (
                  <button
                    className="maint-btn-small"
                    onClick={() => onEdit(row.key, row.value ?? "")}
                    title="Edit via changeConfiguration"
                  >
                    <Edit2 size={10} /> Edit
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {unknown.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 11, color: "#888" }}>
          Unknown keys: {unknown.join(", ")}
        </div>
      )}
    </>
  );
};

const GetLocalListVersionRenderer: React.FC<{
  data: Record<string, unknown>;
  onSeed?: (next: number) => void;
}> = ({ data, onSeed }) => {
  const version = data.list_version as number | undefined;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <span style={{
        padding: "8px 16px",
        background: "#ede8fd",
        color: "#4040a0",
        borderRadius: 8,
        fontSize: 20,
        fontWeight: 700,
      }}>
        v{version ?? "?"}
      </span>
      {onSeed && version != null && (
        <button className="maint-btn-small" onClick={() => onSeed(version + 1)}>
          Send Update (v{version + 1})
        </button>
      )}
    </div>
  );
};

interface SchedulePeriod {
  start_period?: number;
  limit?: number;
  number_phases?: number;
}

const GetCompositeScheduleRenderer: React.FC<{ data: Record<string, unknown> }> = ({ data }) => {
  const status = data.status as string;
  const schedule = data.charging_schedule as
    | { duration?: number; start_schedule?: string; charging_rate_unit?: string; charging_schedule_period?: SchedulePeriod[] }
    | undefined;
  const periods = schedule?.charging_schedule_period ?? [];
  const maxLimit = Math.max(1, ...periods.map((p) => p.limit ?? 0));

  return (
    <>
      <div className="rcmd-response-kv">
        <dt>Status</dt><dd><StatusPill status={status} /></dd>
        {schedule && (
          <>
            <dt>Connector</dt><dd>{(data.connector_id as number | undefined) ?? "—"}</dd>
            <dt>Rate unit</dt><dd>{schedule.charging_rate_unit ?? "—"}</dd>
            <dt>Duration</dt><dd>{schedule.duration ?? "—"} s</dd>
            <dt>Start</dt><dd>{schedule.start_schedule ?? "—"}</dd>
          </>
        )}
      </div>

      {periods.length > 0 && (
        <>
          <div className="rcmd-section-label" style={{ marginTop: 12 }}>Periods</div>
          <table className="rcmd-response-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Start (s)</th>
                <th>Limit ({schedule?.charging_rate_unit ?? "?"})</th>
                <th>Phases</th>
                <th>Visual</th>
              </tr>
            </thead>
            <tbody>
              {periods.map((p, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td>{p.start_period ?? 0}</td>
                  <td>{p.limit ?? "—"}</td>
                  <td>{p.number_phases ?? "—"}</td>
                  <td>
                    <div style={{
                      height: 6,
                      width: `${Math.max(8, ((p.limit ?? 0) / maxLimit) * 100)}%`,
                      background: "#5554d3",
                      borderRadius: 3,
                    }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
};

const TransactionStartRenderer: React.FC<{ data: Record<string, unknown> }> = ({ data }) => {
  const status = data.status as string;
  return (
    <div className="rcmd-response-kv">
      <dt>Status</dt><dd><StatusPill status={status} /></dd>
      <dt>Next</dt>
      <dd style={{ color: "#666", fontSize: 12 }}>
        Watch live logs for the matching <code>StartTransaction</code> event — that's where the transaction_id is assigned.
      </dd>
    </div>
  );
};

const DiagnosticsRenderer: React.FC<{ data: Record<string, unknown> }> = ({ data }) => {
  const fileName = data.file_name as string | undefined;
  return (
    <div className="rcmd-response-kv">
      <dt>File name</dt><dd><code>{fileName ?? "—"}</code></dd>
      <dt>Progress</dt>
      <dd style={{ color: "#666", fontSize: 12 }}>
        Watch <code>DiagnosticsStatusNotification</code> events in the live log to track upload.
      </dd>
    </div>
  );
};

const UpdateFirmwareRenderer: React.FC = () => (
  <div style={{ color: "#666", fontSize: 13 }}>
    Acknowledged. Track <code>FirmwareStatusNotification</code> events in the live log below.
  </div>
);

const DataTransferRenderer: React.FC<{ data: Record<string, unknown> }> = ({ data }) => {
  const status = data.status as string;
  const replyData = data.data as string | undefined;
  let parsed: unknown = null;
  if (replyData) {
    try {
      parsed = JSON.parse(replyData);
    } catch {
      parsed = null;
    }
  }
  return (
    <div className="rcmd-response-kv">
      <dt>Status</dt><dd><StatusPill status={status} /></dd>
      {replyData != null && (
        <>
          <dt>Reply data</dt>
          <dd>
            {parsed ? (
              <pre style={{
                margin: 0, padding: 8, background: "#1a1a2e",
                color: "#b0d4ff", borderRadius: 4, fontSize: 11, overflow: "auto",
              }}>{JSON.stringify(parsed, null, 2)}</pre>
            ) : (
              <code style={{ fontSize: 12 }}>{replyData}</code>
            )}
          </dd>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main card
// ---------------------------------------------------------------------------

const ResponseCard: React.FC<Props> = ({ result, catalogEntry, onEditConfigKey, onSeedSendLocalList }) => {
  const isDeviceError = result.status === "device_error";
  const errorPayload = isDeviceError ? result.response : null;
  const data = result.response ?? {};
  const rawStatus = (data.status as string | undefined) ?? (isDeviceError ? "Failed" : "—");

  const renderBody = () => {
    if (isDeviceError) {
      return (
        <div style={{ color: "#c62828", fontSize: 13 }}>
          {(errorPayload?.error as string) ?? "Device error"}
          {errorPayload?.error_detail ? (
            <div style={{ color: "#666", fontSize: 12, marginTop: 4 }}>
              {errorPayload.error_detail as string}
            </div>
          ) : null}
        </div>
      );
    }

    switch (result.action) {
      case "getConfiguration":
        return <GetConfigurationRenderer data={data} onEdit={onEditConfigKey} />;
      case "getLocalListVersion":
        return <GetLocalListVersionRenderer data={data} onSeed={onSeedSendLocalList} />;
      case "getCompositeSchedule":
        return <GetCompositeScheduleRenderer data={data} />;
      case "startRemoteTransaction":
      case "stopRemoteTransaction":
        return <TransactionStartRenderer data={data} />;
      case "getDiagnostics":
        return <DiagnosticsRenderer data={data} />;
      case "updateFirmware":
        return <UpdateFirmwareRenderer />;
      case "dataTransfer":
        return <DataTransferRenderer data={data} />;
      default: {
        // Generic: render any extra fields as a key/value table.
        const entries = Object.entries(data).filter(
          ([k]) => k !== "status" && k !== "int_event_id" && k !== "_latency_ms",
        );
        if (entries.length === 0) return null;
        return (
          <div className="rcmd-response-kv">
            {entries.map(([k, v]) => (
              <React.Fragment key={k}>
                <dt>{k}</dt>
                <dd>{typeof v === "object" ? JSON.stringify(v) : String(v)}</dd>
              </React.Fragment>
            ))}
          </div>
        );
      }
    }
  };

  return (
    <div className="rcmd-response">
      <div className="rcmd-response-header">
        <span style={{ fontSize: 13, fontWeight: 500 }}>
          {catalogEntry?.title ?? result.action}
        </span>
        <StatusPill
          status={isDeviceError ? "Failed" : (rawStatus || "OK")}
          kind={isDeviceError ? "rejected" : undefined}
        />
        <Latency ms={result.latency_ms} />
      </div>
      {renderBody()}
      <RawJson data={data} />
    </div>
  );
};

export default ResponseCard;
